import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { environment } from '../../environments/environment';
import { FormatDecimalString, UserAccountModel } from 'starbase-launcher';
import { AppComponent } from '../app.component';
import { LU_SubscriptionPriceModel_ext } from '../models/lu_subscriptionprice';
import { ExternalScriptService } from '../services/external-script.service';
import { FortisSubscriptionService } from '../services/fortis-subscription.service';
import { LuSubscriptionDiscountService } from '../services/lu-subscriptiondiscount.service';
import { SquarePaymentRequest } from '../models/paymentRequest';
import { UserSubscriptionModel_ext } from '../models/userSubscription';
import { StarbaseNotificationSentiment, StarbaseNotificationService } from 'starbase-notification';
import { FortisManagePaymentComponent } from '../fortis-manage-payment/fortis-manage-payment.component';
import { UserDataService } from '../services/user-data.service';

declare const window: any;

const ALLSET_MESSAGE = 'All set! Your subscription is ready.';

@Component({
  selector: 'app-fortis-payment-form',
  templateUrl: './fortis-payment-form.component.html',
  styleUrls: ['./fortis-payment-form.component.css']
})
export class FortisPaymentFormComponent implements OnInit {
  @Input() darkMode = false; // Fortis JS form supports this. TODO: Enable custom style to the Fortis form
  @Input() tooltipDelay = 100;
  @Input() chosenPlan: LU_SubscriptionPriceModel_ext;
  @Input() userAccount: UserAccountModel;
  @Input() signupMode = false;
  @Input() currencySymbol = '$';
  @Input() isHandset = false;

  @Output() successfullySubscribed = new EventEmitter();

  @ViewChild('paymentManager') paymentManager: FortisManagePaymentComponent;

  public subscriberTermsText: string;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public ALLSET_MESSAGE = ALLSET_MESSAGE;

  displayPaymentAmt = '';
  displayOriginalAmt = '';
  paymentReady = true;
  credentialError = false;
  showPaymentStatus = false;
  discountCode = '';
  agreeToTerms = false;

  alreadySubscribed = false;

  discountAmount = 0.00;
  paymentAmount: number;

  cardStatus: boolean;

  captchaVerified = false;
  siteKey = environment.recaptcha.siteKey;

  paymentStatus: string;
  badCode: boolean = undefined;
  paymentProcessing: boolean;
  collapseAnyway = false;
  constructor(private scriptSvc: ExternalScriptService,
              private fortisSvc: FortisSubscriptionService,
              private discountSvc: LuSubscriptionDiscountService,
              private userSvc: UserDataService,
              private notiSvc: StarbaseNotificationService,
              private appCmp: AppComponent) {

   }

   get displayDiscountAmt(): string {
    if (this.badCode) {
      return '0.00';
    }
    return (this.discountAmount > 0 ? '-' : '') + FormatDecimalString(this.discountAmount, 2);
  }

  ngOnInit() {
    if (!this.userAccount) {
      throw new Error('Payment form: No valid user account was loaded.');
    }
    if (!this.chosenPlan) {
      throw new Error('Payment form: No valid subscription plan was loaded.');
    }
    this.subscriberTermsText = `By checking this box, you agree to be charged 
        ${this.chosenPlan.cadenceDisp.key} for access to the Research Arsenal site. Cancel anytime after the first payment is made.`;
      this.setRegularPaymentAmt();
      this.displayOriginalAmt = FormatDecimalString(this.paymentAmount, 2);
      this.alreadySubscribed = (
        this.userAccount.subscriptionRecord
        && this.userAccount.subscriptionRecord.subscriptionExpires > new Date()
        && !this.userAccount.subscriptionRecord.canceledDateTime
      );
    console.log('Waiting on Fortis server...');
  }

  resolvedRecaptcha(event) {
    if (event) {
      this.userSvc.verifyRecaptcha(event).subscribe(result => {
        if (result) {
          this.captchaVerified = true;
        } else {
          this.notiSvc.postNotification(this.notiSvc.notificationBuilder(
            'Failed Verification',
            'Please refresh and try again.', undefined, undefined,
            StarbaseNotificationSentiment.Error
          ));
        }
      });
    }
  }

  setCardStatus(set: boolean) {
    this.cardStatus = set;
    if (this.cardStatus === true && this.chosenPlan.price === 0) {
      this.successfullySubscribed.emit();
    }
  }

  private setRegularPaymentAmt() {
    this.paymentAmount = this.chosenPlan.price +
      (this.chosenPlan.taxable ? (this.chosenPlan.price * this.chosenPlan.taxRate) : 0);
    this.displayPaymentAmt = FormatDecimalString(this.paymentAmount, 2);
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  checkBlankDiscount(): void {
    if (this.discountCode === '') {
      this.checkDiscountCode();
    }
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  checkDiscountCode(): void {
    if (this.discountCode === '') {
      this.badCode = undefined;
      this.discountAmount = 0.00;
      // reset payment amt
      this.setRegularPaymentAmt();
      return;
    }
    this.paymentReady = false;
    this.setRegularPaymentAmt();
    this.discountSvc.getDiscountByCode(this.discountCode, this.chosenPlan.uid).subscribe(
      (d) => {
        this.badCode = true;
        if (d.discountFixedAmount > 0) {
          this.discountAmount = d.discountFixedAmount;
          this.paymentAmount -= this.discountAmount;
          this.badCode = false;
        } else if (d.discountPercentage > 0) {
          this.discountAmount = (this.paymentAmount * (d.discountPercentage / 100)); // d.discountPercentage;
          this.paymentAmount = this.paymentAmount - this.discountAmount;
          this.badCode = false;
        }
        this.displayPaymentAmt = FormatDecimalString(this.paymentAmount, 2);
        this.paymentReady = true;
      }, // TODO: Unit tests for money-related things
      (e) => {
        console.error(e);
        this.discountAmount = 0.00;
        this.badCode = undefined;
        this.setRegularPaymentAmt();
        this.paymentReady = true;
      }
    );
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  async handlePaymentMethodSubmission(event) {
    event.preventDefault();
    if (this.credentialError) {
      return;
    }
    if (!this.cardStatus) {
      return;
    }
    try {
      // disable the submit button as we await tokenization and make a payment request.
      this.paymentReady = false;
      this.paymentProcessing = true;
      if (this.appCmp.paymentModal) {
        this.appCmp.paymentModal.working = true;
      }
      // const token = await this.tokenize(paymentMethod);
      this.createPayment();

    } catch (e) {
      this.paymentReady = true; // allow trying again
      this.displayPaymentResults('FAILURE');
      this.paymentProcessing = false;
      if (this.appCmp.paymentModal) {
        this.appCmp.paymentModal.working = false;
      }
      console.error(e.message);
    }
  }

  // status is either SUCCESS or FAILURE;
  // eslint-disable-next-line @typescript-eslint/member-ordering
  displayPaymentResults(status) { // TODO: update this to 'Angular' style
    const statusContainer = document.getElementById(
      'payment-status-container'
    );
    if (status === 'SUCCESS') {
      statusContainer.classList.remove('is-failure');
      statusContainer.classList.add('is-success');
      this.paymentStatus = ALLSET_MESSAGE;
      this.successfullySubscribed.emit();
    } else {
      statusContainer.classList.remove('is-success');
      statusContainer.classList.add('is-failure');
      this.paymentStatus = 'Sorry, there was an error processing your payment.';
    }
    statusContainer.style.visibility = 'visible';
    this.showPaymentStatus = true;
  }

  private createPayment() {
    const body: SquarePaymentRequest = {
      paymentTokenObj: {
        loc: '',
        // Use `displayPaymentAmt` here to ensure we are checking against what was stringified and shown on screen.
        statedPrice: parseFloat(this.displayPaymentAmt.replace(this.currencySymbol, ''))
      },
      discountCode: this.badCode ? '' : this.discountCode, // any discount code string to check eligibility on server
      idempotencyKey: '',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      card_id: '',
      userAccount: this.userAccount,
      subscriptionPrice: this.chosenPlan
    };

    this.showPaymentStatus = false;
    if (this.userAccount.subscriptionRecord != null) { // then change endpoint
      this.fortisSvc.changeFortisSubscription(body).subscribe(
        (resp) => this.handlePurchaseSuccess(resp),
        (e) => this.handlePurchaseFailure(e));
    } else { // otherwise new or returning subscriber, so create
      this.fortisSvc.createFortisSubscription(body, this.signupMode).subscribe(
        (resp) => this.handlePurchaseSuccess(resp),
        (e) => this.handlePurchaseFailure(e));
    }

  }

  private handlePurchaseSuccess(resp: UserSubscriptionModel_ext): void {
    // console.debug(resp);
    this.displayPaymentResults('SUCCESS');
    this.paymentProcessing = false;
    if (this.appCmp.paymentModal) {
      this.appCmp.paymentModal.working = false;
    }
  }

  private handlePurchaseFailure(e): void {
    this.paymentReady = true; // allow trying again
    this.displayPaymentResults('FAILURE');
    console.error(e);
    this.paymentProcessing = false;
    this.notiSvc.postNotification(this.notiSvc.notificationBuilder(
      'Could not create subscription',
      `Sorry, there was an error when attempting to create your subscription. Please review your information and try again.
      If the problem persist please Contact our support team at ${environment.supportEmailAddress}. Error Reason: ${e.serverMsg[0]}`,
      undefined, 10000,
      StarbaseNotificationSentiment.Error
    ));
    if (this.appCmp.paymentModal) {
      this.appCmp.paymentModal.working = false;
    }
    if (this.signupMode) {
      this.paymentManager.cardData = null;
      this.paymentManager.setupCardEntryForm();
    }
  }
}
